/**
 * ExperienceAndRisk -- Profile section: experience tier, acknowledged
 * strategies, and activity download. Modal copy comes from the server.
 */
import React, { useCallback, useEffect, useState } from 'react';
import * as svc from '../../services/experienceService';
import type { ExperienceLevel, ExperienceState, AckModalCopy } from '../../services/experienceService';

const LEVELS: ExperienceLevel[] = ['beginner', 'intermediate', 'expert'];

const TIER_INFO: Record<ExperienceLevel, { name: string; line: string }> = {
  beginner: { name: 'Beginner', line: 'Shows covered calls, cash-secured puts, long options and defined-risk spreads.' },
  intermediate: { name: 'Intermediate', line: 'Also shows jade lizards, iron condors and short strangles.' },
  expert: { name: 'Expert', line: 'Shows every strategy, including naked puts, naked calls and ratio spreads.' },
};

// One place for plain-English strategy names.
const STRATEGY_NAMES: Record<string, string> = {
  naked_put: 'Naked put',
  naked_call: 'Naked call',
  short_strangle: 'Short strangle',
  jade_lizard: 'Jade lizard',
  ratio_spread: 'Ratio spread',
  zero_dte: 'Same-day expiration (0 DTE)',
  custom_symbol: 'Custom symbol',
  live_mode_flip: 'Switching an engine to live trading',
};
const strategyName = (k: string) =>
  STRATEGY_NAMES[k] || k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Lowest tier that shows each flagged strategy. Mirrors TIER_INFO above (no
// server-side mapping exists): Intermediate adds jade lizards and short
// strangles; Expert adds naked puts, naked calls and ratio spreads. Same-day
// expiration, custom symbols and live trading are not tier-gated.
const STRATEGY_MIN_TIER: Record<string, ExperienceLevel> = {
  naked_put: 'expert',
  naked_call: 'expert',
  ratio_spread: 'expert',
  short_strangle: 'intermediate',
  jade_lizard: 'intermediate',
  zero_dte: 'beginner',
  custom_symbol: 'beginner',
  live_mode_flip: 'beginner',
};
const STRATEGY_ORDER = Object.keys(STRATEGY_MIN_TIER);

const etDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) : null;

function plainError(e: any): string {
  switch (e?.code) {
    case 'TIER_CHANGED_CONCURRENTLY':
      return 'Your tier changed in another window. Refresh and try again.';
    case 'ACK_REQUIRED':
      return 'Please read and acknowledge the risks to continue.';
    case 'CAPITAL_MINIMUM_REQUIRED':
      return 'Please confirm the capital minimum to continue.';
    case 'INVALID_LEVEL':
      return 'That tier is not available.';
    case 'EXPORT_TOO_LARGE':
      return 'Your activity is too large to export here. Please contact support and we will send it to you.';
    default:
      return e?.status === 409
        ? 'Your tier changed in another window. Refresh and try again.'
        : 'Something went wrong. Please try again.';
  }
}

const panel: React.CSSProperties = { padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', background: 'rgba(255,255,255,0.03)', marginBottom: '1rem' };
const btn = (bg: string): React.CSSProperties => ({ padding: '0.5rem 1rem', backgroundColor: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' });
const small: React.CSSProperties = { color: '#94a3b8', fontSize: '0.8rem' };

interface AckModalProps {
  copy: AckModalCopy;
  needsCapital: boolean;
  busy: boolean;
  error?: string | null;
  onConfirm: (capitalAcked: boolean) => void;
  onCancel: () => void;
}

export const AckModal: React.FC<AckModalProps> = ({ copy, needsCapital, busy, error, onConfirm, onCancel }) => {
  const [capital, setCapital] = useState(false);
  const blocked = busy || (needsCapital && !capital);
  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#1e293b', color: '#e2e8f0', padding: '1.5rem', borderRadius: '8px', maxWidth: '520px', width: '90%' }}>
        <h3 style={{ color: '#ffffff', marginTop: 0 }}>{copy.title}</h3>
        <p style={{ lineHeight: 1.5 }}>{copy.body}</p>
        {needsCapital && copy.checkboxLabel && (
          <label style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={capital} onChange={(e) => setCapital(e.target.checked)} />
            <span>{copy.checkboxLabel}</span>
          </label>
        )}
        {error && <p role="alert" style={{ color: '#fca5a5' }}>{error}</p>}
        <div style={{ marginTop: '1rem' }}>
          <button style={{ ...btn('#3b82f6'), opacity: blocked ? 0.5 : 1 }} disabled={blocked} onClick={() => onConfirm(capital)}>
            {busy ? 'Saving...' : copy.confirmLabel}
          </button>
          <button style={btn('#475569')} disabled={busy} onClick={onCancel}>{copy.cancelLabel}</button>
        </div>
      </div>
    </div>
  );
};

const ExperienceAndRisk: React.FC<{ token: string | null }> = ({ token }) => {
  const [state, setState] = useState<ExperienceState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [upgradeTo, setUpgradeTo] = useState<ExperienceLevel | null>(null);
  const [downgradeTo, setDowngradeTo] = useState<ExperienceLevel | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [ackingKey, setAckingKey] = useState<string | null>(null);
  const [exp, setExp] = useState<svc.ExportResult | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setState(await svc.getExperience(token));
      setLoadError(null);
    } catch (e) {
      setLoadError(plainError(e));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loadError) return <p style={{ color: '#fca5a5' }}>{loadError}</p>;
  if (!state) return <p style={{ color: '#cbd5e1' }}>Loading...</p>;

  // Unknown/missing level from the server falls back to Beginner display.
  const level: ExperienceLevel = LEVELS.includes(state.experienceLevel) ? state.experienceLevel : 'beginner';
  const idx = LEVELS.indexOf(level);
  const up = idx < LEVELS.length - 1 ? LEVELS[idx + 1] : null;
  const down = idx > 0 ? LEVELS[idx - 1] : null;
  const upCopy = upgradeTo ? state.ackCopy.tiers[upgradeTo] : undefined;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); } catch (e) { setError(plainError(e)); } finally { setBusy(false); }
  };

  // Errors from the upgrade call are shown inside the modal (the page-level
  // error would render underneath the overlay).
  const confirmUpgrade = async (capital: boolean) => {
    if (!upgradeTo || !upCopy) return;
    setBusy(true); setError(null); setNotice(null); setModalError(null);
    try {
      await svc.setLevel(token as string, upgradeTo, {
        ackModalVersion: upCopy.version,
        ...(upgradeTo === 'expert' ? { capitalMinimumAcked: capital } : {}),
      });
      setUpgradeTo(null);
      setNotice(`You are now on the ${TIER_INFO[upgradeTo].name} tier.`);
      await load();
    } catch (e: any) {
      if (e?.code === 'ACK_REQUIRED') {
        // Stale copy/version: reload so the modal shows the current wording.
        await load();
        setModalError('The wording was updated. Please review it and confirm again.');
      } else {
        setModalError(plainError(e));
      }
    } finally { setBusy(false); }
  };

  const confirmDowngrade = () => downgradeTo && run(async () => {
    await svc.setLevel(token as string, downgradeTo);
    setNotice(`You are now on the ${TIER_INFO[downgradeTo].name} tier.`);
    setDowngradeTo(null);
    await load();
  });

  const confirmStrategyAck = async () => {
    const copy = ackingKey ? state.ackCopy.strategies[ackingKey] : undefined;
    if (!ackingKey || !copy) return;
    setBusy(true); setError(null); setNotice(null); setModalError(null);
    try {
      await svc.recordAck(token as string, ackingKey, copy.version);
      setNotice(`Saved. You acknowledged: ${strategyName(ackingKey)}.`);
      setAckingKey(null);
      await load();
    } catch (e: any) {
      if (e?.code === 'STALE_MODAL_VERSION') {
        await load();
        setModalError('The wording was updated. Please review it and confirm again.');
      } else {
        setModalError(plainError(e));
      }
    } finally { setBusy(false); }
  };

  const confirmRemove = () => removing && run(async () => {
    await svc.removeAck(token as string, removing);
    setRemoving(null);
    await load();
  });

  const doExport = () => run(async () => {
    const r = await svc.exportActivity(token as string);
    setExp(r);
    window.open(r.url, '_blank', 'noopener');
  });

  const acks = Object.entries(state.strategyAcks || {});
  const ackCopies = state.ackCopy?.strategies || {};
  const visibleStrategies = STRATEGY_ORDER.filter(
    (k) => ackCopies[k] && LEVELS.indexOf(STRATEGY_MIN_TIER[k]) <= idx
  );
  const statusOf = (k: string): { text: string; done: boolean } => {
    const stored = (state.strategyAcks || {})[k];
    if (!stored || !stored.modalVersion) return { text: 'Not acknowledged', done: false };
    if (stored.modalVersion !== ackCopies[k].version) return { text: 'Needs re-acknowledgement', done: false };
    return { text: `Acknowledged on ${etDate(stored.ackedAt) || 'an earlier date'}`, done: true };
  };
  const ackingCopy = ackingKey ? ackCopies[ackingKey] : undefined;

  return (
    <div>
      <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginTop: '-0.25rem' }}>
        Your tier controls which strategies are shown to you. It is a convenience setting, and you can change it at any time.
      </p>
      {error && <p role="alert" style={{ color: '#fca5a5' }}>{error}</p>}
      {notice && <p style={{ color: '#86efac' }}>{notice}</p>}

      <div style={panel}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ ...btn('#2563eb'), cursor: 'default', display: 'inline-block' }}>{TIER_INFO[level].name}</span>
          <span style={{ color: '#cbd5e1' }}>{TIER_INFO[level].line}</span>
        </div>
        {up && !downgradeTo && <button style={btn('#3b82f6')} disabled={busy} onClick={() => { setError(null); setModalError(null); setUpgradeTo(up); }}>Upgrade to {TIER_INFO[up].name}</button>}
        {down && !downgradeTo && <button style={btn('#475569')} disabled={busy} onClick={() => { setError(null); setDowngradeTo(down); }}>Downgrade to {TIER_INFO[down].name}</button>}
        {downgradeTo && (
          <div style={{ color: '#e2e8f0' }}>
            <p>Switch to {TIER_INFO[downgradeTo].name}? Some strategies will be hidden from you.</p>
            <button style={btn('#3b82f6')} disabled={busy} onClick={confirmDowngrade}>Yes, switch</button>
            <button style={btn('#475569')} disabled={busy} onClick={() => setDowngradeTo(null)}>Cancel</button>
          </div>
        )}
        {level === 'expert' && state.capitalMinimumAcked && (
          <p style={small}>You confirmed the capital minimum for undefined-risk strategies.</p>
        )}
      </div>

      <h3 style={{ color: '#ffffff', fontSize: '1rem' }}>Strategies you have acknowledged</h3>
      {acks.length === 0 ? (
        <p style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>None yet. You will be asked the first time you use a higher-risk strategy.</p>
      ) : (
        <div style={panel}>
          {acks.map(([key, a]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', color: '#e2e8f0' }}>
              <div>
                <div>{strategyName(key)}</div>
                {a?.ackedAt && <div style={small}>acknowledged on {etDate(a.ackedAt)}</div>}
              </div>
              <button style={btn('#7f1d1d')} disabled={busy} onClick={() => { setError(null); setRemoving(key); }}>Remove</button>
            </div>
          ))}
          {removing && (
            <div style={{ color: '#e2e8f0', marginTop: '0.5rem' }}>
              <p>You will be asked to acknowledge the risks again next time you use this strategy.</p>
              <button style={btn('#7f1d1d')} disabled={busy} onClick={confirmRemove}>Remove {strategyName(removing)}</button>
              <button style={btn('#475569')} disabled={busy} onClick={() => setRemoving(null)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {visibleStrategies.length > 0 && (
        <>
          <h3 style={{ color: '#ffffff', fontSize: '1rem' }}>Strategy risk acknowledgements</h3>
          <div style={panel}>
            {visibleStrategies.map((k) => {
              const st = statusOf(k);
              return (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', color: '#e2e8f0' }}>
                  <div>
                    <div>{strategyName(k)}</div>
                    <div style={small}>{st.text}</div>
                  </div>
                  {!st.done && (
                    <button style={btn('#3b82f6')} disabled={busy} onClick={() => { setError(null); setModalError(null); setAckingKey(k); }}>
                      Acknowledge
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <h3 style={{ color: '#ffffff', fontSize: '1rem' }}>Your activity</h3>
      <button style={btn('#3b82f6')} disabled={busy} onClick={doExport}>Download my activity (CSV)</button>
      {exp && (
        <p style={small}>
          Link valid for 1 hour. If the download did not start, <a href={exp.url} target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>open it here</a>.
          <br />SHA-256: {exp.sha256}
        </p>
      )}

      {ackingKey && ackingCopy && (
        <AckModal
          key={ackingKey}
          copy={ackingCopy}
          needsCapital={!!ackingCopy.checkboxLabel}
          busy={busy}
          error={modalError}
          onConfirm={confirmStrategyAck}
          onCancel={() => setAckingKey(null)}
        />
      )}

      {upgradeTo && upCopy && (
        <AckModal
          copy={upCopy}
          needsCapital={upgradeTo === 'expert'}
          busy={busy}
          error={modalError}
          onConfirm={confirmUpgrade}
          onCancel={() => setUpgradeTo(null)}
        />
      )}
    </div>
  );
};

export default ExperienceAndRisk;
